import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { post } from "./helper/api_helper";
import "./Dashboard.css";

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const STATUS_TABS = ["All", "Arrived", "Pending", "Despatch"];

const STATUS_BADGE = {
  "ARRIVED": "rs-badge-arrived",
  "PENDING": "rs-badge-pending",
  "BE FILED": "rs-badge-filed",
  "IN TRANSIT": "rs-badge-transit",
  "DESPATCH": "rs-badge-dispatch",
};

const KPI_META = [
  { icon: "📦", label: "Total Active Jobs", key: "total_active_jobs", valueColor: "var(--accent)", subKey: "active_jobs_change", subSuffix: " vs last week" },
  { icon: "🚢", label: "Containers Arrived", key: "containers_arrived_month", valueColor: "var(--success)", subKey: "arrived_change", subSuffix: " this month" },
  { icon: "⏳", label: "BE Pending Filing", key: "be_pending", valueColor: "var(--warn)", subKey: "be_overdue_today", subSuffix: " overdue today" },
  { icon: "🚛", label: "Trucks Not Reached", key: "trucks_not_reached", valueColor: "var(--danger)", subKey: "trucks_not_reached", subSuffix: " need follow-up" },
];

const BAR_COLORS = ["var(--danger)", "var(--warn)", "var(--accent)", "var(--success)", "var(--purple)", "var(--text2)"];
const BAR_GRADIENTS = [
  "linear-gradient(90deg,var(--danger),#e85c8a)",
  "linear-gradient(90deg,var(--warn),#f0b040)",
  "linear-gradient(90deg,var(--accent),#00aaff)",
  "linear-gradient(90deg,var(--success),#30c880)",
  "linear-gradient(90deg,var(--purple),#9c88ff)",
  "linear-gradient(90deg,var(--text2),#aaa)",
];
const DOT_COLORS = ["var(--success)", "var(--accent)", "var(--warn)", "var(--purple)", "var(--danger)"];

// ── SKELETON ──────────────────────────────────────────────────────────────────
const Skeleton = ({ height = 16, width = "100%", style = {} }) => (
  <div style={{ height, width, borderRadius: 6, background: "linear-gradient(90deg,var(--card2) 25%,var(--border) 50%,var(--card2) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", ...style }} />
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const Dashboard = () => {

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [barsAnimated, setBarsAnimated] = useState(false);

  const [kpi, setKpi] = useState(null);
  const [dailyJobs, setDailyJobs] = useState([]);
  const [cfsStatus, setCfsStatus] = useState(null);
  const [liveActivity, setLiveActivity] = useState([]);
  const [despatch, setDespatch] = useState(null);
  const [trucksNR, setTrucksNR] = useState([]);

  const [loadingKpi, setLoadingKpi] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [loadingCfs, setLoadingCfs] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loadingDespatch, setLoadingDespatch] = useState(true);
  const [loadingTrucks, setLoadingTrucks] = useState(true);

  // ── Alert Helper ──────────────────────────────────────────────────────────
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({ icon, title, text: text || "Something went wrong.", timer, showConfirmButton: false });
  }, []);

  // ── Fetch KPI ─────────────────────────────────────────────────────────────
  const fetchKpi = useCallback(async () => {
    setLoadingKpi(true);
    try {
      const response = await post("/dashboard/kpi");
      if (response.status === 200) {
        setKpi(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoadingKpi(false);
    }
  }, [showAlert]);

  // ── Fetch Daily Status ────────────────────────────────────────────────────
  const fetchDailyStatus = useCallback(async (statusFilter = "all") => {
    setLoadingDaily(true);
    try {
      const response = await post("/dashboard/daily_status", { status: statusFilter });
      if (response.status === 200) {
        setDailyJobs(response.data?.jobs ?? response.data ?? []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoadingDaily(false);
    }
  }, [showAlert]);

  // ── Fetch CFS Status ──────────────────────────────────────────────────────
  const fetchCfsStatus = useCallback(async () => {
    setLoadingCfs(true);
    try {
      const response = await post("/dashboard/cfs_status");
      if (response.status === 200) {
        setCfsStatus(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoadingCfs(false);
    }
  }, [showAlert]);

  // ── Fetch Live Activity ───────────────────────────────────────────────────
  const fetchLiveActivity = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const response = await post("/dashboard/live_activity");
      if (response.status === 200) {
        setLiveActivity(response.data ?? []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoadingActivity(false);
    }
  }, [showAlert]);

  // ── Fetch Despatch ────────────────────────────────────────────────────────
  const fetchDespatch = useCallback(async () => {
    setLoadingDespatch(true);
    try {
      const response = await post("/dashboard/despatch");
      if (response.status === 200) {
        setDespatch(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoadingDespatch(false);
    }
  }, [showAlert]);

  // ── Fetch Trucks Not Reached ──────────────────────────────────────────────
  const fetchTrucksNotReached = useCallback(async () => {
    setLoadingTrucks(true);
    try {
      const response = await post("/dashboard/trucks_not_reached");
      if (response.status === 200) {
        setTrucksNR(response.data ?? []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoadingTrucks(false);
    }
  }, [showAlert]);

  // ── Initial Load ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchKpi();
    fetchCfsStatus();
    fetchLiveActivity();
    fetchDespatch();
    fetchTrucksNotReached();
    const t = setTimeout(() => setBarsAnimated(true), 600);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tab drives daily status (runs on mount for "all" + on every tab click) ─
  useEffect(() => {
    const statusMap = ["all", "arrived", "pending", "despatch"];
    fetchDailyStatus(statusMap[activeTab]);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-refresh live activity every 30s ──────────────────────────────────
  useEffect(() => {
    const interval = setInterval(fetchLiveActivity, 30_000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ────────────────────────────────────────────────────────
  const cfsLocations = cfsStatus?.locations ?? [];
  const clearancePct = cfsStatus?.clearance_pct ?? 0;
  const cleared = cfsStatus?.cleared ?? 0;
  const inProgress = cfsStatus?.in_progress ?? 0;
  const cfsPending = cfsStatus?.pending ?? 0;
  const despatchRows = despatch?.rows ?? [];
  const despatchTotals = despatch?.totals ?? {};
  const ringDot = 2 * Math.PI * 36;
  const ringOffset = ringDot - (clearancePct / 100) * ringDot;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rs-body">
      <div className="rs-layout">
        <div className="rs-main">
          <div className="rs-content">

            {/* KPI CARDS */}
            <div className="rs-kpi-grid">
              {KPI_META.map((k, i) => (
                <div className="rs-kpi-card" key={i}>
                  <div className="rs-kpi-icon">{k.icon}</div>
                  <div className="rs-kpi-label">{k.label}</div>
                  {loadingKpi ? (
                    <>
                      <Skeleton height={30} width="60%" style={{ margin: "8px 0" }} />
                      <Skeleton height={13} width="80%" />
                    </>
                  ) : (
                    <>
                      <div className="rs-kpi-value" style={{ color: k.valueColor }}>{kpi?.[k.key] ?? "—"}</div>
                      <div className="rs-kpi-sub">
                        <span style={{ color: String(kpi?.[k.subKey] ?? "").startsWith("-") ? "var(--danger)" : "var(--success)" }}>
                          {kpi?.[k.subKey] ?? ""}
                        </span>
                        &nbsp;{k.subSuffix}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* ROW 2 */}
            <div className="rs-three-col">

              {/* Daily Status */}
              <div className="rs-panel" style={{ animationDelay: "0.25s" }}>
                <div className="rs-panel-header">
                  <div className="rs-panel-title">Daily Status Report</div>
                </div>
                <div className="rs-tabs">
                  {STATUS_TABS.map((t, i) => (
                    <button key={i} className={`rs-tab${activeTab === i ? " active" : ""}`} onClick={() => setActiveTab(i)}>{t}</button>
                  ))}
                </div>
                <div className="rs-table-wrap">
                  <table className="rs-table">
                    <thead>
                      <tr><th>#</th><th>JOB NO</th><th>CONSIGNEE</th><th>BL NO</th><th>CTRS</th><th>STATUS</th><th>DEM UPTO</th></tr>
                    </thead>
                    <tbody>
                      {loadingDaily
                        ? Array(5).fill(0).map((_, i) => (
                          <tr key={i}>{Array(7).fill(0).map((_, j) => <td key={j}><Skeleton height={13} /></td>)}</tr>
                        ))
                        : dailyJobs.map((r, i) => (
                          <tr key={i}>
                            <td className="rs-mono">{String(i + 1).padStart(2, "0")}</td>
                            <td className="rs-bold rs-mono">{r.job_no}</td>
                            <td>{r.consignee}</td>
                            <td className="rs-mono">{r.bl_number}</td>
                            <td className="rs-mono">{r.containers}</td>
                            <td><span className={`rs-badge ${STATUS_BADGE[r.status] ?? ""}`}>{r.status}</span></td>
                            <td className="rs-mono" style={{ color: r.dem_color ?? "var(--text2)" }}>{r.dem_upto}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>

              {/* CFS Status */}
              <div className="rs-panel" style={{ animationDelay: "0.3s" }}>
                <div className="rs-panel-header">
                  <div className="rs-panel-title">CFS Arrival Status</div>
                  <span className="rs-tag rs-tag-green">LIVE</span>
                </div>
                <div className="rs-cfs-grid">
                  {loadingCfs
                    ? Array(6).fill(0).map((_, i) => (
                      <div className="rs-cfs-card" key={i}>
                        <Skeleton height={13} width={60} style={{ margin: "0 auto 8px" }} />
                        <Skeleton height={28} width={40} style={{ margin: "0 auto 6px" }} />
                        <Skeleton height={10} width={70} style={{ margin: "0 auto" }} />
                      </div>
                    ))
                    : cfsLocations.map((c, i) => (
                      <div className="rs-cfs-card" key={i} title={c.cfs_name}>
                        <div className="rs-cfs-name">{c.cfs_code || c.cfs_name}</div>
                        <div className="rs-cfs-count" style={{ color: BAR_COLORS[i % BAR_COLORS.length] }}>{c.container_count}</div>
                        <div className="rs-cfs-label">CONTAINERS</div>
                      </div>
                    ))
                  }
                </div>
                <div style={{ borderTop: "1px solid var(--border)", padding: "16px 20px", background: "var(--card2)" }}>
                  <div style={{ fontSize: 10, letterSpacing: "1.5px", color: "var(--text3)", fontFamily: "'DM Mono',monospace", marginBottom: 12, textTransform: "uppercase" }}>
                    Clearance Progress
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div className="rs-ring-wrap">
                      <svg width="90" height="90">
                        <circle cx="45" cy="45" r="36" fill="none" stroke="var(--border2)" strokeWidth="6" />
                        <circle cx="45" cy="45" r="36" fill="none" stroke="var(--accent)" strokeWidth="6"
                          strokeDasharray={ringDot} strokeDashoffset={loadingCfs ? ringDot : ringOffset}
                          strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
                      </svg>
                      <div className="rs-ring-label">
                        <div className="rs-ring-val" style={{ color: "var(--accent)" }}>{clearancePct}%</div>
                        <div className="rs-ring-sub">CLEARED</div>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      {[["Cleared", cleared, "var(--success)"], ["In Progress", inProgress, "var(--warn)"], ["Pending", cfsPending, "var(--danger)"]].map(([label, val, color]) => (
                        <div className="rs-ring-info-row" key={label} style={{ marginBottom: label === "Pending" ? 0 : undefined }}>
                          <span style={{ color: "var(--text3)", fontSize: 12 }}>{label}</span>
                          {loadingCfs
                            ? <Skeleton height={13} width={30} />
                            : <span style={{ fontWeight: 600, color, fontSize: 12 }}>{val}</span>
                          }
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Live Activity */}
              <div className="rs-panel" style={{ animationDelay: "0.35s" }}>
                <div className="rs-panel-header">
                  <div className="rs-panel-title">Live Activity</div>
                  <span className="rs-tag rs-tag-red">REAL-TIME</span>
                </div>
                <div className="rs-activity-list">
                  {loadingActivity
                    ? Array(7).fill(0).map((_, i) => (
                      <div className="rs-activity-item" key={i}>
                        <div className="rs-activity-dot" style={{ background: "var(--border2)" }} />
                        <div style={{ flex: 1 }}>
                          <Skeleton height={13} style={{ marginBottom: 5 }} />
                          <Skeleton height={10} width={60} />
                        </div>
                      </div>
                    ))
                    : liveActivity.map((a, i) => (
                      <div className="rs-activity-item" key={i}>
                        <div className="rs-activity-dot" style={{ background: a.color ?? DOT_COLORS[i % DOT_COLORS.length] }} />
                        <div>
                          <div className="rs-activity-text" dangerouslySetInnerHTML={{ __html: a.text }} />
                          <div className="rs-activity-time">{a.time ?? a.created_at}</div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            {/* ROW 3 */}
            <div className="rs-two-col">

              {/* Despatch */}
              <div className="rs-panel" style={{ animationDelay: "0.4s" }}>
                <div className="rs-panel-header">
                  <div className="rs-panel-title">Daily Despatch Report</div>
                  <span className="rs-tag rs-tag-orange">TODAY</span>
                  <button className="rs-btn-ghost">↓ PDF</button>
                </div>
                <div className="rs-table-wrap rs-table_high">
                  <table className="rs-table">
                    <thead>
                      <tr><th>#</th><th>LORRY NO</th><th>CONTAINER</th><th>PKGS</th><th>WEIGHT</th><th>FREIGHT</th><th>GC NO</th></tr>
                    </thead>
                    <tbody>
                      {loadingDespatch
                        ? Array(4).fill(0).map((_, i) => (
                          <tr key={i}>{Array(7).fill(0).map((_, j) => <td key={j}><Skeleton height={13} /></td>)}</tr>
                        ))
                        : despatchRows.map((r, i) => (
                          <tr key={i}>
                            <td className="rs-mono">{String(i + 1).padStart(2, "0")}</td>
                            <td className="rs-bold rs-mono">{r.lorry}</td>
                            <td className="rs-mono">{r.container}</td>
                            <td className="rs-mono">{r.packages}</td>
                            <td className="rs-mono">{r.weight}</td>
                            <td className="rs-mono" style={{ color: "var(--success)" }}>{r.freight}</td>
                            <td className="rs-mono">{r.gc_no}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Right column */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Trucks Not Reached */}
                <div className="rs-panel" style={{ animationDelay: "0.5s" }}>
                  <div className="rs-panel-header">
                    <div className="rs-panel-title">Trucks Not Reached</div>
                    <span className="rs-tag rs-tag-red">ACTION NEEDED</span>
                  </div>
                  <div className="rs-table-wrap rs-table_high">
                    <table className="rs-table">
                      <thead>
                        <tr><th>JOB NO</th><th>BL NO</th><th>TRUCK</th><th>DRIVER</th><th>AGE</th></tr>
                      </thead>
                      <tbody>
                        {loadingTrucks
                          ? Array(3).fill(0).map((_, i) => (
                            <tr key={i}>{Array(5).fill(0).map((_, j) => <td key={j}><Skeleton height={13} /></td>)}</tr>
                          ))
                          : trucksNR.map((r, i) => (
                            <tr key={i}>
                              <td className="rs-bold rs-mono">{r.job_no}</td>
                              <td className="rs-mono">{r.bl_number}</td>
                              <td className="rs-mono" style={{ color: "var(--warn)" }}>{r.lorry}</td>
                              <td className="rs-mono">{r.driver_phone}</td>
                              <td className="rs-mono" style={{ color: r.age_color ?? "var(--danger)" }}>{r.age}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`@keyframes shimmer { to { background-position: -200% 0 } }`}</style>
    </div>
  );
};

export default Dashboard;